'use client';

import { useState, useEffect, useCallback } from 'react';

import axiosInstance, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

export type MenyInställning = { nyckel: string; synlig: boolean; roller?: string[] };

type MenyInställningarSvar = { inställningar: MenyInställning[] };

/**
 * Bygger en synlighetskarta (nyckel → synlig) som bakåtkompatibilitet.
 */
export function tillSynlighetskarta(
  inställningar: MenyInställning[]
): Record<string, boolean> {
  return inställningar.reduce<Record<string, boolean>>((acc, i) => {
    acc[i.nyckel] = i.synlig;
    return acc;
  }, {});
}

/**
 * Bygger en samlad inställningskarta (nyckel → { synlig, roller }) som `filtreraNavigering`
 * använder för både synlighets- och rollkontroll.
 */
export function tillInställningskarta(
  inställningar: MenyInställning[]
): Record<string, MenyInställning> {
  return inställningar.reduce<Record<string, MenyInställning>>((acc, i) => {
    acc[i.nyckel] = i;
    return acc;
  }, {});
}

/**
 * Pågående hämtningar per organisation.
 *
 * Hooken anropas av flera komponenter samtidigt — sidomenyn, ruttspärren, Kom igång och
 * Att göra-listan, och i hubbläget en gång per förening. Utan den här delningen blev det
 * ett anrop per konsument vid varje sidladdning. Bara *pågående* anrop delas, inget svar
 * sparas: en sparad inställning ska slå igenom direkt när `uppdatera` körs.
 */
const pågående = new Map<string, Promise<MenyInställning[]>>();

function hämtaDelat(orgId: string): Promise<MenyInställning[]> {
  const redanIgång = pågående.get(orgId);
  if (redanIgång) {
    return redanIgång;
  }

  const begäran = axiosInstance
    .get<MenyInställningarSvar>(endpoints.organisation.menyinställningar(orgId))
    .then((res) => res.data?.inställningar ?? [])
    .finally(() => {
      pågående.delete(orgId);
    });

  pågående.set(orgId, begäran);
  return begäran;
}

/**
 * Hämtar en organisations menyinställningar. Fel sväljs medvetet – navigeringen
 * ska falla tillbaka på "allt synligt" om anropet misslyckas.
 */
export function useMenyInställningar(orgId?: string) {
  const [inställningar, setInställningar] = useState<MenyInställning[]>([]);
  const [loading, setLoading] = useState(false);
  const [laddadFörOrg, setLaddadFörOrg] = useState<string | undefined>(undefined);

  const hämta = useCallback(async () => {
    if (!orgId) {
      setInställningar([]);
      return;
    }
    setLoading(true);
    try {
      setInställningar(await hämtaDelat(orgId));
    } catch (error) {
      console.error('Kunde inte hämta menyinställningar', error);
      setInställningar([]);
    } finally {
      setLoading(false);
      setLaddadFörOrg(orgId);
    }
  }, [orgId]);

  useEffect(() => {
    hämta();
  }, [hämta]);

  /**
   * Om `inställningar` hör till den organisation anroparen frågade om.
   *
   * `loading` duger inte: den är `false` vid första renderingen, så en tom lista
   * ser då inläst ut. Den som fattar beslut utifrån inställningarna — ruttspärren
   * — skulle hinna dra slutsatsen att inget är påslaget och skicka användaren bort
   * från en sida organisationen faktiskt har öppnad. Villkoret jämförs mot orgId i
   * stället för att lagras, så ett organisationsbyte aldrig ser klart för tidigt.
   */
  const klar = !orgId || laddadFörOrg === orgId;

  return { inställningar, loading, klar, uppdatera: hämta };
}
